import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { adminClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { setScoreError, TEAM_NAME } from "@/lib/dvStats";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function EndSetDialog({ open, onOpenChange, match, setNumber, onSaved }) {
  const { t } = useT();
  const [us, setUs] = useState("");
  const [them, setThem] = useState("");
  const [saving, setSaving] = useState(false);
  const error = us !== "" && them !== "" ? setScoreError(setNumber, Number(us), Number(them), t) : null;

  useEffect(() => {
    if (!open) return;
    const sc = match.set_scores?.[String(setNumber)];
    setUs(sc ? String(sc[0]) : "");
    setThem(sc ? String(sc[1]) : "");
  }, [open, match, setNumber]);

  const submit = async (e) => {
    e.preventDefault();
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const set_scores = { ...(match.set_scores || {}), [String(setNumber)]: [Number(us) || 0, Number(them) || 0] };
      const { data } = await adminClient.patch(`/datavolley/matches/${match.id}`, { set_scores });
      toast.success(`SET ${setNumber}: ${us} - ${them}`);
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, value, setter, testid) => (
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-bold tracking-widest text-slate-500 truncate text-center">{label}</p>
      <input
        type="number"
        min={0}
        max={99}
        inputMode="numeric"
        required
        value={value}
        onChange={(e) => setter(e.target.value)}
        onFocus={(e) => e.target.select()}
        data-testid={testid}
        className="mt-1.5 h-20 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 text-center font-heading font-black text-5xl text-slate-900 focus:outline-none focus:border-ocean"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="dv-end-set-dialog">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-sunset/15 text-sunset flex items-center justify-center mb-1">
            <Flag className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center font-heading font-black text-2xl">
            {t("dv_end_set")} · SET {setNumber}
          </DialogTitle>
          <DialogDescription className="text-center">{t("dv_end_set_hint")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-5">
          <div className="flex items-end gap-3">
            {field(TEAM_NAME, us, setUs, "dv-end-set-us")}
            <span className="font-heading font-black text-3xl text-slate-300 pb-5">-</span>
            {field(match.opponent, them, setThem, "dv-end-set-them")}
          </div>
          {error && (
            <p className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-semibold px-3 py-2 text-center" data-testid="dv-end-set-error">
              {error}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="dv-end-set-cancel" className="dv-btn h-12 rounded-xl bg-slate-100 text-slate-700 font-bold">
              {t("dv_cancel")}
            </button>
            <button type="submit" disabled={saving || !!error} data-testid="dv-end-set-save" className="dv-btn h-12 rounded-xl bg-sunset text-white font-bold disabled:opacity-50">
              {t("dv_save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
